import { Analytics } from '@symposium/ui-utilities';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import ReactPlayer from 'react-player';
import { Maximize16 } from '@carbon/icons-react';
import LightBox from '../LightBox';
const EVENT_MAP = {
  STARTED: {
    videoPlayerState: 'played',
    videoPlayerStatus: 0
  },
  PAUSED: {
    videoPlayerState: 'paused',
    videoPlayerStatus: 1
  },
  PLAYED: {
    videoPlayerState: 'played',
    videoPlayerStatus: 2
  },
  ENDED: {
    videoPlayerState: 'ended',
    videoPlayerStatus: 3
  },
  ERROR: {
    videoPlayerState: 'error',
    videoPlayerStatus: 4
  }
};

const trackEvt = (evt, {
  url = 'N/A - url not provided'
}, player) => {
  if (!player) return Analytics.trackVideoInteraction({ ...EVENT_MAP[evt],
    url
  });
  const totalLength = player.player.getDuration();
  const percentagePlayed = `${Math.round(player.player.getCurrentTime() / totalLength * 100)}%`;
  return Analytics.trackVideoInteraction({ ...EVENT_MAP[evt],
    url,
    percentagePlayed,
    totalLength
  });
};

function VideoPlayer(props) {
  const {
    className: customClassName,
    thumbnailUrl,
    initialized,
    fullscreenOnly,
    allowFullscreen,
    forwardedRef,
    mountId,
    ...otherProps
  } = props;
  const thumbnailPreview = initialized ? false : thumbnailUrl || true;
  let playerRef = null;
  const [isFullscreen, setIsFullscreen] = React.useState(fullscreenOnly || false);
  const playerProps = {
    ref: forwardedRef,
    playIcon: /*#__PURE__*/React.createElement("button", {
      "aria-label": "Play video",
      className: "om-video-player__play-btn",
      tabIndex: "0"
    }),
    config: {
      vimeo: {
        playerOptions: {
          controls: true
        }
      },
      youtube: {
        playerVars: {
          controls: 0
        }
      }
    },
    className: cx('om-video-player', {
      [customClassName]: !!customClassName
    }),
    light: thumbnailPreview,
    onReady: (...args) => {
      playerRef = args && args.length ? args[0] : null; // Note: assume first args element is always a player ref
    },
    onError: () => trackEvt('ERROR', props, playerRef),
    onStart: () => trackEvt('STARTED', props, playerRef),
    onPlay: () => trackEvt('PLAYED', props, playerRef),
    onPause: () => trackEvt('PAUSED', props, playerRef),
    onEnded: () => trackEvt('ENDED', props, playerRef),
    ...otherProps
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, isFullscreen && /*#__PURE__*/React.createElement(LightBox, {
    onRequestClose: () => setIsFullscreen(false),
    mountId: mountId
  }), !fullscreenOnly || isFullscreen ? /*#__PURE__*/React.createElement("div", {
    className: cx({
      'om-video-player__wrapper': !isFullscreen,
      'om-video-player__wrapper--FULLSCREEN': isFullscreen
    })
  }, allowFullscreen && !isFullscreen ? /*#__PURE__*/React.createElement("button", {
    "aria-label": "Fullscreen video",
    className: "om-video-player__fullscreen-btn",
    onClick: () => setIsFullscreen(true)
  }, /*#__PURE__*/React.createElement(Maximize16, null)) : null, /*#__PURE__*/React.createElement(ReactPlayer, playerProps)) : null);
}

VideoPlayer.defaultProps = {
  playing: true,
  muted: false,
  width: '100%',
  height: '100%',
  initialized: false,
  fullscreenOnly: false,
  allowFullscreen: true
}; // See https://github.com/CookPete/react-player#props for all available props.

VideoPlayer.propTypes = {
  controls: PropTypes.bool,
  playing: PropTypes.bool,
  muted: PropTypes.bool,
  width: PropTypes.string,
  height: PropTypes.string,
  initialized: PropTypes.bool,
  thumbnailUrl: PropTypes.string,
  url: PropTypes.string.isRequired,
  fullscreenOnly: PropTypes.bool,
  allowFullscreen: PropTypes.bool,
  // Passed to the lightbox component
  mountId: PropTypes.string
};
export default VideoPlayer;